import { apiClient } from './config.js';

export interface Operator {
  firebase_uid: string;
  email: string | null;
  display_name: string | null;
  roles: string[];
  status: 'active' | 'disabled';
  receives_moderation_alerts: boolean;
}

export interface OperatorListResponse {
  operators: Operator[];
}

export async function getMe(): Promise<Operator> {
  const { data } = await apiClient.get<Operator>('/operator/me');
  return data;
}

export async function updateMe(body: { display_name?: string | null }): Promise<Operator> {
  const { data } = await apiClient.patch<Operator>('/operator/me', body);
  return data;
}

export async function listOperators(): Promise<OperatorListResponse> {
  const { data } = await apiClient.get<OperatorListResponse>('/admin/operators');
  return data;
}

export async function inviteOperator(body: {
  email: string;
  roles: string[];
  /** Optional explicit initial password. When omitted, a random password is
   *  generated and the standard reset-link flow handles credential delivery.
   *  Use this when the email-loop infra (custom Action URL, verified sender
   *  domain) isn't ready yet — admin communicates the password out-of-band. */
  initial_password?: string;
}): Promise<Operator> {
  const { data } = await apiClient.post<Operator>('/admin/operators', body);
  return data;
}

export async function updateOperator(
  firebase_uid: string,
  body: { roles?: string[]; status?: 'active' | 'disabled'; receives_moderation_alerts?: boolean }
): Promise<Operator> {
  const { data } = await apiClient.patch<Operator>(`/admin/operators/${firebase_uid}`, body);
  return data;
}

/** Re-send the branded invite (backend generates link + sends via Mailjet). */
export async function resendInvite(firebase_uid: string): Promise<{ sent: boolean; email: string }> {
  const { data } = await apiClient.post<{ sent: boolean; email: string }>(
    `/admin/operators/${firebase_uid}/resend-invite`,
  );
  return data;
}

/** Public forgot-password: backend emails a reset link (always 200, no enumeration). */
export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post('/operator/forgot-password', { email });
}
